import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, Printer, RotateCcw, Share2, Trash2 } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { isActive, projectNameOf, runShortId, runTitle } from '@/lib/format';
import { runPlanLabel } from '@/lib/groups';
import { ReportResults, ReportSummary, RunProgressCard } from '@/components/report/ReportView';
import { ShareDialog } from '@/components/report/ShareDialog';
import { LiveView } from '@/components/report/LiveView';
import { CommentsPanel } from '@/components/report/CommentsPanel';
import { WatchLiveToggle } from '@/components/report/WatchLiveToggle';
import { liveToggleState, useWatchLivePreference } from '@/lib/liveView';
import { describeModel } from '@/lib/perf';
import { PerfLive } from '@/components/perf/PerfLive';
import { PerfReport } from '@/components/perf/PerfReport';
import { SecurityReport, SecurityChecks } from '@/components/report/SecurityReport';
import { Button, ErrorNotice, PageHeader, PageLoader, StatusPill, useConfirm, useToast } from '@/components/ui';

export function RunReportPage() {
  const { runId = '' } = useParams();
  const { can, canModify } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);

  const run = useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.run(runId),
    // Live updates while the run is in progress.
    refetchInterval: (q) => (q.state.data && isActive(q.state.data.status) ? 3000 : false),
  });

  const orgId = run.data?.project?.orgId ?? null;
  // Opened through a share: read and comment only, whatever the org role says.
  const sharedOnly = run.data?.access?.sharedOnly ?? false;
  const canTrigger = !sharedOnly && can('run.trigger', orgId);
  // The project (browser setting) decides whether live view is possible for "Run again".
  const project = useQuery({
    queryKey: ['project', run.data?.projectId],
    queryFn: () => api.project(run.data!.projectId),
    enabled: Boolean(run.data?.projectId) && canTrigger,
  });
  const [watchLive, setWatchLive] = useWatchLivePreference();
  const live = liveToggleState(project.data, watchLive);
  // Keep the live panel (last frame + "Run finished") on screen after a run watched on this page ends.
  const [liveShown, setLiveShown] = useState(false);
  const watchable = Boolean(run.data?.liveView && isActive(run.data.status));
  useEffect(() => {
    if (watchable) setLiveShown(true);
  }, [watchable]);
  const canShare = !sharedOnly && can('report.share', orgId);
  // Only the person who started the run (or a Project Admin) deletes the report or its videos.
  const canDelete = !sharedOnly && canModify('report.delete', run.data?.triggeredBy?.id, orgId);
  const canOpenProject = !sharedOnly && !!orgId && can('project.read', orgId);

  const cancel = useMutation({
    mutationFn: () => api.cancelRun(runId),
    onSuccess: () => {
      toast.info('Run cancelled');
      qc.invalidateQueries({ queryKey: ['run', runId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const del = useMutation({
    mutationFn: () => api.deleteRun(runId),
    onSuccess: () => {
      toast.success('Report deleted');
      qc.removeQueries({ queryKey: ['run', runId] });
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['runs', run.data?.projectId] });
      navigate(canOpenProject && run.data ? `/projects/${run.data.projectId}?tab=runs` : '/reports');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const deleteArtifact = useMutation({
    mutationFn: ({ resultId, name }: { resultId: string; name: string }) => api.deleteArtifact(resultId, name),
    onSuccess: () => {
      toast.success('Video deleted');
      qc.invalidateQueries({ queryKey: ['run', runId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rerun = useMutation({
    mutationFn: () => {
      // A performance run: the same test with the same workload (or another check run), same test data.
      const plan = run.data?.perfPlan;
      if (plan && !plan.testCaseId) throw new Error('Check the script again from its page (Scripts).');
      if (plan?.testCaseId) return api.triggerPerfRun(run.data!.projectId, plan.testCaseId, plan.verify ? { verify: true } : plan.load ? { load: plan.load } : {}, run.data?.collectionId ?? null);
      // A group run runs the group again (its current tests and settings); otherwise the same tests.
      // Same test data collection (environment) as this run.
      const collectionId = run.data?.collectionId ?? null;
      if (run.data?.groupId) return api.runGroup(run.data.groupId, { liveView: live.checked, collectionId });
      const ids = run.data?.results?.map((r) => r.testCaseId);
      return api.triggerRun(run.data!.projectId, ids && ids.length ? ids : undefined, { liveView: live.checked, collectionId });
    },
    onSuccess: (r) => {
      toast.success('New run started');
      navigate(`/runs/${r.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (run.isPending) return <PageLoader />;
  if (run.isError) return <ErrorNotice error={run.error} onRetry={() => run.refetch()} />;
  const data = run.data;
  const perf = data.perfPlan ?? null;
  const security = data.securityPlan ?? null;

  return (
    <div className="space-y-6">
      <Link
        to={canOpenProject ? `/projects/${data.projectId}?tab=runs` : '/reports'}
        className="no-print inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> Back
      </Link>
      <PageHeader
        eyebrow={projectNameOf(data)}
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {runTitle(data)} <StatusPill status={data.status} />
          </span>
        }
        description={
          <>
            <span className="font-mono" title={`Run ID: ${data.id}`}>
              Run {runShortId(data)}
            </span>
            {data.testNames && data.testNames.length > 2 && ` · ${data.testNames.length} tests`}
            {runPlanLabel(data.plan) && ` · ${runPlanLabel(data.plan)}`}
            {data.collectionName && ` · ${data.collectionName} test data`}
            {data.appBuildLabel && ` · build ${data.appBuildLabel}`}
            {data.trigger && ` · started by ${data.trigger.kind === 'SCHEDULE' ? 'schedule' : 'webhook'} “${data.trigger.name}”`}
            {perf && ` · ${describeModel(perf.model)} on ${perf.agentIds.length} load agent${perf.agentIds.length === 1 ? '' : 's'}`}
            {isActive(data.status) ? ' · This run is in progress - the page updates automatically.' : ''}
          </>
        }
        actions={
          <div className="no-print flex flex-wrap gap-2">
            {isActive(data.status) && canTrigger && (
              <Button variant="secondary" onClick={() => cancel.mutate()} loading={cancel.isPending} icon={<Ban className="size-4" />}>
                {perf ? 'Stop the load' : 'Cancel'}
              </Button>
            )}
            {!isActive(data.status) && canTrigger && !perf && !security && <WatchLiveToggle state={live} onChange={setWatchLive} />}
            {!isActive(data.status) && canTrigger && (
              <Button variant="secondary" onClick={() => rerun.mutate()} loading={rerun.isPending} icon={<RotateCcw className="size-4" />}>
                {data.groupId ? 'Run group again' : 'Run again'}
              </Button>
            )}
            <Button variant="ghost" onClick={() => window.print()} icon={<Printer className="size-4" />}>
              Print
            </Button>
            {canShare && (
              <Button onClick={() => setShareOpen(true)} icon={<Share2 className="size-4" />}>
                Share
              </Button>
            )}
            {canDelete && !isActive(data.status) && (
              <Button
                variant="danger"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Delete this report?',
                    message: (
                      <>
                        <strong className="font-semibold text-slate-900 dark:text-white">{runTitle(data)}</strong> (run {runShortId(data)}) and its
                        screenshots and videos will be deleted. This cannot be undone.
                      </>
                    ),
                    confirmLabel: 'Delete report',
                  });
                  if (ok) del.mutate();
                }}
                loading={del.isPending}
                icon={<Trash2 className="size-4" />}
              >
                Delete
              </Button>
            )}
          </div>
        }
      />
      {perf && isActive(data.status) && <PerfLive key={data.id} runId={data.id} runStatus={data.status} onEnd={() => qc.invalidateQueries({ queryKey: ['run', runId] })} />}
      {perf && !isActive(data.status) && <PerfReport run={data} result={data.results?.[0]} title={runTitle(data)} />}
      {perf && <CommentsPanel runId={data.id} />}
      {security && (
        <>
          {isActive(data.status) ? (
            // Live: a progress bar and the checklist of checks as they run (no A–F grade until it finishes).
            <SecurityChecks steps={data.progress?.steps ?? []} active />
          ) : data.status === 'PASSED' || data.status === 'FAILED' ? (
            <>
              <SecurityReport findings={data.results?.[0]?.findings ?? []} plan={security} />
              <SecurityChecks steps={data.results?.[0]?.steps ?? []} />
            </>
          ) : (
            // The scan did not finish with a grade (scanner error, cancelled). Show why, not a reassuring
            // Grade A — an empty findings list would otherwise summarise to A. Still show what ran.
            <>
              <div className="rounded-card bg-danger-subtle p-4 ring-1 ring-border ring-inset">
                <p className="text-sm font-semibold text-danger-text">
                  {data.status === 'CANCELLED' ? 'This scan was cancelled before it finished — no security grade.' : 'The scan did not complete, so there is no security grade.'}
                </p>
                {(data.results?.[0]?.error ?? data.error) && (
                  <pre className="mt-2 overflow-x-auto text-xs whitespace-pre-wrap text-danger-text">{data.results?.[0]?.error ?? data.error}</pre>
                )}
              </div>
              <SecurityChecks steps={data.results?.[0]?.steps ?? []} />
            </>
          )}
          {data.results?.[0]?.error && (data.status === 'PASSED' || data.status === 'FAILED') && (
            <p className="rounded-field bg-danger-subtle p-3 text-sm text-danger-text">{data.results[0].error}</p>
          )}
          <CommentsPanel runId={data.id} />
        </>
      )}
      {!perf && !security && data.liveView && (isActive(data.status) || liveShown) && (
        <LiveView
          key={data.id}
          runId={data.id}
          runStatus={data.status}
          onFinished={() => {
            qc.invalidateQueries({ queryKey: ['run', runId] });
          }}
        />
      )}
      {!perf && !security && isActive(data.status) && <RunProgressCard run={data} />}
      {!perf && !security && <ReportSummary run={data} />}
      {!perf && !security && <CommentsPanel runId={data.id} />}
      {!perf && !security && <ReportResults
        run={data}
        onDeleteArtifact={
          canDelete && !isActive(data.status)
            ? (resultId, name) => deleteArtifact.mutate({ resultId, name })
            : undefined
        }
      />}
      {canShare && <ShareDialog runId={data.id} open={shareOpen} onClose={() => setShareOpen(false)} />}
    </div>
  );
}
